<template>
  <v-row justify="center">
    <v-dialog
      origin="top center"
      v-model="customerPropertyDialog"
      :max-width="dialogWidth"
      content-class="customer-property-dialog"
      persistent
    >
      <v-card>
        <!-- Header -->
        <v-card-title
          class="headline dialog-custom-header white-background custom-border-bottom custom-header-blue-text d-flex align-center justify-space-between w-100"
        >
          <div class="d-flex align-center">
            <v-avatar size="38" color="cyan lighten-5" class="mr-3">
              <v-icon color="cyan darken-2">mdi-map-marker-outline</v-icon>
            </v-avatar>
            <div class="dialog-title color-custom-blue">
              Select a
              <template v-if="addressType">{{ addressType }}</template>
              <template v-else>Site</template> Location
            </div>
          </div>
          <v-btn
            icon
            small
            class="custom-grey-border bg-slate-50"
            v-on:click="
              $emit('close', true);
              $emit('resetAll', true);
            "
          >
            <v-icon color="slate-600" small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <!-- Search Toolbar -->
          <v-card flat outlined class="rounded-lg bg-slate-50 px-3 py-1 mb-3">
            <v-row dense align="center">
              <v-col cols="12" sm="12" md="12" class="py-0">
                <label
                  for="search-customer-property-input"
                  class="font-size-13 font-weight-600 mb-0"
                >
                  Which
                  <template v-if="addressType"
                    >{{ addressType }} location</template
                  >
                  <template v-else>Service location</template> would you like to
                  create this for?
                </label>
              </v-col>
              <v-col cols="12" sm="8" md="8">
                <v-text-field
                  id="search-customer-property-input"
                  ref="search-customer-property"
                  v-model.trim="customerProperty"
                  :loading="searchLoader"
                  :placeholder="placeholder"
                  autocomplete="off"
                  dense
                  filled
                  solo
                  flat
                  hide-details
                  color="cyan"
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  @blur="isFocused = false"
                  @focus="isFocused = true"
                  @keydown.esc="onEsc"
                  @keydown.enter="onEnter"
                  @keydown.tab="onTab"
                  class="custom-erp-input bg-white rounded-lg"
                />
              </v-col>
              <v-col cols="12" sm="4" md="4" class="d-flex align-center">
                <v-btn
                  v-on:click="searchCustomerProperty()"
                  :loading="searchLoader"
                  :disabled="searchLoader"
                  depressed
                  color="cyan darken-2 white--text"
                  class="custom-bold-button w-100"
                  height="36"
                >
                  <v-icon small left>mdi-magnify</v-icon>
                  Search
                </v-btn>
              </v-col>
            </v-row>
          </v-card>

          <!-- Property List Area -->
          <perfect-scrollbar
            :options="{ suppressScrollX: true }"
            class="scroll pr-1"
            style="max-height: calc(100vh - 320px); position: relative"
          >
            <div v-if="searchLoader">
              <v-skeleton-loader
                class="custom-skeleton my-2"
                type="list-item-two-line"
              />
            </div>
            <div
              v-else-if="customerPropertyList && customerPropertyList.length"
            >
              <v-card
                v-for="(data, index) in customerPropertyList"
                :key="`parent-${index}`"
                flat
                outlined
                class="rounded-lg px-3 py-0 mb-2 bg-white hover-shadow cursor-pointer transition-fast"
                v-on:click="selectCustomerProperty(data)"
              >
                <v-row dense align="center">
                  <!-- Left: Location Details -->
                  <v-col cols="12" md="8" class="py-1">
                    <div class="d-flex align-center flex-wrap gap-2 mb-1">
                      <Barcode :barcode="data.barcode" />
                      <v-chip
                        v-if="data.is_tenant"
                        class="ml-1"
                        label
                        small
                        color="green"
                        outlined
                        style="height: 22px; font-size: 12px"
                      >
                        Tenant
                      </v-chip>
                      <v-chip
                        label
                        small
                        class="ml-1"
                        :color="data.type == 1 ? 'cyan' : 'orange'"
                        outlined
                        style="height: 22px; font-size: 12px"
                      >
                        <template v-if="data.type == 1">Site Location</template>
                        <template v-else>Billing</template>
                      </v-chip>
                    </div>

                    <div
                      class="font-weight-700 font-size-14 color-custom-blue mb-1 d-flex align-center custom-nowrap-ellipsis"
                    >
                      <v-icon small left color="slate-600"
                        >mdi-map-marker-outline</v-icon
                      >
                      <span>
                        <span v-if="data?.property_name"
                          >{{ data.property_name }} - </span
                        >{{ getFormattedProperty(data) }}
                      </span>
                    </div>
                  </v-col>

                  <!-- Right: Select CTA -->
                  <v-col
                    cols="12"
                    md="4"
                    class="text-md-right d-flex align-center justify-md-end py-1"
                  >
                    <v-btn
                      small
                      depressed
                      color="cyan lighten-5"
                      class="custom-bold-button px-3 cyan--text text-darken-2"
                    >
                      Select Location
                      <v-icon small right>mdi-arrow-right</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card>
            </div>

            <!-- Empty State -->
            <div
              v-else
              class="d-flex flex-column align-center justify-center py-10 px-4 text-center border rounded-lg bg-slate-50 border-dashed"
            >
              <v-avatar size="56" color="grey lighten-4" class="mb-3">
                <v-icon color="slate-400" large
                  >mdi-map-marker-off-outline</v-icon
                >
              </v-avatar>
              <div class="font-weight-700 font-size-15 text-slate-800 mb-1">
                No Service Location(s) Found
              </div>
              <div class="caption-1 text-muted max-w-300">
                There are no matching location records in the database.
              </div>
            </div>
          </perfect-scrollbar>
        </v-card-text>
      </v-card>
    </v-dialog>

    <Dialog
      v-if="createPropertyDialog"
      :commonDialog="createPropertyDialog"
      :dialogWidth="768"
    >
      <template v-slot:title>Create Site Location</template>
      <template v-slot:body>
        <v-form
          ref="createProperty"
          v-model.trim="formValid"
          lazy-validation
          v-on:submit.stop.prevent="createProperty"
        >
          <DialogPropertyAddress
            :isDialog="true"
            v-on:savePropertyAddress="updateProperty"
          >
          </DialogPropertyAddress>
        </v-form>
      </template>
      <template v-slot:action>
        <v-btn
          depressed
          :disabled="!formValid || formLoading"
          :loading="formLoading"
          class="mx-2 custom-bold-button white--text"
          color="cyan"
          v-on:click="createProperty"
          >Save
        </v-btn>
        <v-btn
          depressed
          :disabled="formLoading"
          class="mx-2 custom-grey-border custom-bold-button"
          v-on:click="createPropertyDialog = false"
          >Close
        </v-btn>
      </template>
    </Dialog>
  </v-row>
</template>

<style scoped>
.v-dialog__content {
  align-items: start;
}
</style>

<script>
import CustomerMixin from "@/core/plugins/customer-mixin";
import Dialog from "@/view/pages/partials/Dialog.vue";
import { PUT, CLEAR_ERROR } from "@/core/services/store/request.module";
import DialogPropertyAddress from "@/view/pages/customer/create-or-update/Property-Address.vue";
import Barcode from "@/view/pages/partials/Barcode.vue";

export default {
  mixins: [CustomerMixin],
  data() {
    return {
      formValid: true,
      formLoading: false,
      isFocused: false,
      searchLoader: true,
      customerProperty: null,
      timeoutLimit: 500,
      timeout: null,
      propertyAddress: new Object(),
      createPropertyDialog: false,
      customerPropertyList: new Array(),
    };
  },
  watch: {
    customerProperty(param) {
      if (!param) {
        const _this = this;
        clearTimeout(_this.timeout);
        _this.timeout = setTimeout(function () {
          _this.searchCustomerProperty();
        }, _this.timeoutLimit);
      }
    },
    customer() {
      this.searchCustomerProperty(null);
    },
  },
  props: {
    disabledAutoSelect: {
      type: Boolean,
      default: false,
    },
    customerPropertyDialog: {
      type: Boolean,
      required: true,
      default: false,
    },
    dialogWidth: {
      type: Number,
      default: 768,
    },
    addressType: {
      type: String,
      default: null,
    },
    customer: {
      type: Number,
      required: true,
    },
  },
  components: {
    Dialog,
    Barcode,
    DialogPropertyAddress,
  },
  methods: {
    onEsc() {
      this.resetSearch();
    },
    onEnter() {
      if (this.isSearching) {
        this.searchCustomerProperty();
      }
    },
    onTab() {
      if (this.isSearching) {
        this.searchCustomerProperty();
      }
    },
    resetSearch() {
      const _this = this;
      _this.$nextTick(() => {
        _this.customerProperty = null;
        _this.isFocused = false;
      });
    },
    searchCustomerProperty() {
      const _this = this;
      _this.searchLoader = true;
      _this
        .getCustomerProperties(_this.customer, _this.customerProperty)
        .then((response) => {
          _this.customerPropertyList = response;
          if (
            !_this.disabledAutoSelect &&
            _this.customerPropertyList.length === 1
          ) {
            _this.$emit(
              "selectCustomerProperty",
              _this.customerPropertyList[0].id
            );
          }
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.createPropertyDialog = false;
          _this.searchLoader = false;
        });
    },
    selectCustomerProperty(param) {
      this.$emit("selectCustomerProperty", param.id);
    },
    getFormattedProperty(row) {
      let tempArr = [];
      if (row.unit_no) {
        tempArr.push(row.unit_no);
      }
      if (row.street_1) {
        tempArr.push(row.street_1);
      }
      if (row.street_2) {
        tempArr.push(row.street_2);
      }
      if (row.zip_code) {
        tempArr.push(row.country);
      }
      if (row.zip_code) {
        tempArr.push(row.zip_code);
      }
      /*   if (this.lodash.isEmpty(row.city) === false) {
        tempArr.push(row.city.name);
      }
      if (this.lodash.isEmpty(row.state) === false) {
        tempArr.push(row.state.name);
      }
      if (this.lodash.isEmpty(row.country) === false) {
        tempArr.push(row.country);
      } */
      return tempArr.join(", ");
    },
    updateProperty(param) {
      this.propertyAddress = param;
    },
    createProperty() {
      const _this = this;
      if (!_this.$refs.createProperty.validate()) {
        return false;
      }
      let addressFormData = new Array();
      addressFormData.push(_this.propertyAddress);
      const formData = _this.lodash.pickBy(
        addressFormData,
        _this.lodash.identity
      );

      if (_this.lodash.isEmpty(formData) === false) {
        _this.formLoading = true;
        _this.$store.dispatch(CLEAR_ERROR, {});
        _this.$store
          .dispatch(PUT, {
            url: "address/" + _this.customer,
            data: { address: formData },
          })
          .then(() => {
            _this.searchCustomerProperty();
          })
          .catch((error) => {
            _this.logError(error);
          })
          .finally(() => {
            _this.formLoading = false;
          });
      }
    },
  },
  mounted() {
    this.searchCustomerProperty();
    this.$refs["search-customer-property"].focus();
  },
  computed: {
    isSearching() {
      return this.customerProperty && this.customerProperty.length > 0;
    },
    placeholder() {
      return this.isFocused
        ? "Start Typing then press enter"
        : "Search Service Location...";
    },
  },
};
</script>
